import { useEffect, useState } from 'react';
import { Search, Truck, MapPin, User, AlertTriangle, Eye, ShieldCheck, Cpu, Gauge } from 'lucide-react';
import { formatKm } from '../../utils/formatters';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Card } from '../../components/ui/Card';
import { VehicleStatusBadge } from '../../components/vehicles/VehicleListItem';
import { VehicleGraphic } from '../../components/vehicles/VehicleGraphic';
import { VehicleMaintenanceBars } from '../../components/vehicles/VehicleMaintenanceBars';
import { VehicleDetailModal } from '../../components/vehicles/VehicleDetailModal';
import { useVehicles } from '../../hooks/useVehicles';
import type { Vehicle } from '../../types/vehicle';
import { LoadError } from '../../components/common/LoadError';
import clsx from 'clsx';

const STATUS_FILTERS: { value: Vehicle['status'] | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'in_route', label: 'En Ruta' },
  { value: 'critical_event', label: 'Evento Crítico' },
  { value: 'stopped', label: 'Detenidos' },
  { value: 'no_signal', label: 'Sin Señal' },
  { value: 'offline', label: 'Offline' },
];

export default function VehiclesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Vehicle['status'] | 'all'>('all');
  // Antes: useState + setTimeout propio, una de las 3 implementaciones
  // distintas de "traer vehículos" que registraba PENDIENTES.md/FE-02.
  // Ahora usa la misma fuente que MonitoringPage/DashboardPage.
  const { vehicles, isLoading, error, selectedVehicle, selectVehicle } = useVehicles();

  useEffect(() => {
    document.title = 'Vehículos | Paitron Radar';
  }, []);

  const filtered = vehicles.filter(v => {
    const matchSearch =
      search === '' ||
      v.plate.toLowerCase().includes(search.toLowerCase()) ||
      v.brand.toLowerCase().includes(search.toLowerCase()) ||
      v.model.toLowerCase().includes(search.toLowerCase()) ||
      (v.driverName ?? '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || v.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const inRouteCount = vehicles.filter(v => v.status === 'in_route').length;
  const criticalCount = vehicles.filter(v => v.status === 'critical_event').length;
  const stoppedCount = vehicles.filter(v => v.status === 'stopped').length;

  return (
    <div className="vehicles-page" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      
      {/* Stats header */}
      <div className="vehicles-page__stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(16,185,129,0.12)', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Truck size={22} />
          </div>
          <div>
            <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{inRouteCount}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Vehículos en ruta</span>
          </div>
        </div>

        <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.12)', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle size={22} />
          </div>
          <div>
            <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{criticalCount}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Eventos críticos</span>
          </div>
        </div>

        <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(245,158,11,0.12)', color: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MapPin size={22} />
          </div>
          <div>
            <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{stoppedCount}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Detenidos</span>
          </div>
        </div>

        <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'var(--bg-raised)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={22} />
          </div>
          <div>
            <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{vehicles.length}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Flota total</span>
          </div>
        </div>
      </div>

      {/* Toolbar & Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: '1', minWidth: '280px', maxWidth: '450px' }}>
            <Input
              id="vehicles-search"
              placeholder="Buscar placa, marca, modelo o conductor..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              leftIcon={<Search size={15} />}
            />
          </div>
        </div>

        {/* Filter chips */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              id={`vehicle-filter-${f.value}`}
              className={clsx('monitoring-page__filter-chip', statusFilter === f.value && 'monitoring-page__filter-chip--active')}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vehicle Grid */}
      {isLoading ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <Spinner size="lg" />
        </div>
      ) : error && vehicles.length === 0 ? (
        <LoadError what="los vehículos" error={error} />
      ) : filtered.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
          <Truck size={40} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
          <p style={{ fontSize: '15px', fontWeight: 600 }}>No se encontraron vehículos</p>
          <p style={{ fontSize: '13px' }}>Prueba ajustando los filtros de búsqueda.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
          {filtered.map(vehicle => (
              <Card
                key={vehicle.id}
                hover
                onClick={() => selectVehicle(vehicle.id)}
                style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', cursor: 'pointer', padding: '1.25rem' }}
                id={`vehicle-card-${vehicle.id}`}
              >
                {/* Card Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{vehicle.plate}</h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '2px 0 0 0' }}>{vehicle.brand} {vehicle.model} ({vehicle.year})</p>
                  </div>
                  <VehicleStatusBadge status={vehicle.status} />
                </div>

                {/* issue #122: imagen genérica — el schema Vehicle no trae
                    foto. El badge ya está arriba, así que no se repite. */}
                <VehicleGraphic status={vehicle.status} height="100px" showStatusBadge={false} />

                {/* Metadata */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <User size={14} style={{ color: 'var(--accent)' }} />
                    <span>Conductor: <strong>{vehicle.driverName || 'Sin asignar'}</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Cpu size={14} style={{ color: '#8B5CF6' }} />
                    <span>Sub-unidad: <strong>{vehicle.subUnitName}</strong></span>
                  </div>
                  {/* FE-19: el odómetro del EQUIPO (BE-50), no "km recorridos".
                      Sin dato → «—», nunca «0 km»: null significa "todavía no se
                      sabe". La fecha de medición (`odometer.measuredAt`) NO se
                      muestra por decisión del dueño (2026-09-11) — sigue en el
                      contrato y en el tipo si alguna vez hace falta recuperarla. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Gauge size={14} style={{ color: '#F59E0B' }} />
                    <span>
                      Odómetro: <strong>{vehicle.odometer ? formatKm(vehicle.odometer.km) : '—'}</strong>
                    </span>
                  </div>
                </div>

                {/* FE-23: vencimientos reales. Esta tarjeta llegó a pintar «Vigente
                    (180d)» al 45% en TODO vehículo — no era un fallo de cálculo:
                    `maintenance` era `undefined` siempre y la barra caía al «no
                    vence». Ahora un vehículo sin fechas dice «Sin datos». */}
                <div style={{ background: 'var(--bg-raised)', padding: '0.75rem 0.875rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                  <VehicleMaintenanceBars vehicle={vehicle} mode="card" />
                </div>
                {/* Action Button */}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Eye size={14} />}
                  style={{ width: '100%', marginTop: 'auto' }}
                  onClick={(e) => { e.stopPropagation(); selectVehicle(vehicle.id); }}
                >
                  Ver detalle & mantenimiento
                </Button>
              </Card>
          ))}
        </div>
      )}

      {/* Vehicle Detail Popup */}
      <VehicleDetailModal
        isOpen={selectedVehicle !== null}
        onClose={() => selectVehicle(null)}
        vehicle={selectedVehicle}
      />
    </div>
  );
}
